import { useEffect, useState } from "react";
import {
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Plus,
    Minus,
    Pencil,
    Users,
    ShieldCheck,
    Layers,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import RevisionReassurance from "./RevisionReassurance";

/**
 * Iter-60 + Iter-128 — Script Revision Diff & Approval dialog.
 *
 * Loads the structural diff of the pending revision against its parent
 * active script, lets the owner toggle which buckets to apply, and
 * approves with strict safety contract.
 *
 * Iter-128 changes (Trust pass):
 *  - We now respect `revision_kind`:
 *      "revised_pages"  → no "removed" section, no "mark omitted"
 *                          toggle, banner explains that scenes not in
 *                          the pages are untouched.
 *      "full_script"    → existing behaviour but with explicit warning
 *                          copy + a count of scenes that will be
 *                          omitted, so the user can't miss it.
 *  - Result summary uses friendly copy and clarifies that omissions
 *    are reversible (omitted=True, not deleted).
 */
export default function ScriptRevisionDiffDialog({
    projectId,
    revisionId,
    revisionLabel,
    revisionKind: revisionKindProp,
    open,
    onOpenChange,
    onApproved,
}) {
    const apiBase = useApiBase();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [diff, setDiff] = useState(null);
    const [applyAdded, setApplyAdded] = useState(true);
    const [applyOmitted, setApplyOmitted] = useState(true);
    const [applyHeadings, setApplyHeadings] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    // Iter-157P.18 — persistent apply-phase state. The Apply chain
    // (approve → parse → confirm) can each hit a Cloudflare/origin
    // edge timeout even when the server-side step actually
    // succeeds. We surface the phase as a persistent inline banner
    // inside the dialog so producers always know which step is
    // running and never see only a disappearing toast.
    //
    // Shape: { phase: "approving"|"parsing"|"confirming"|"recovering"|"failed"|"timeout", message?, error? }
    const [applyPhase, setApplyPhase] = useState(null);
    // Iter-157P.11.4 — temporary producer-facing "Copy debug payload"
    // affordance. When the resolver lands scenes in the removed
    // bucket the producer didn't expect (real-world DOCX↔PDF case),
    // they can copy the full diagnostic JSON in one tap and paste it
    // back. Lets us tune the resolver against actual extraction
    // signatures instead of agent-side guesses.
    const [debugCopied, setDebugCopied] = useState(false);

    // Resolved revision_kind, preferring the server's response over the
    // prop hint (prop is just an early-paint optimisation).
    const revisionKind =
        (diff?.revision_kind || revisionKindProp || "full_script").toLowerCase();
    const isPages = revisionKind === "revised_pages";

    useEffect(() => {
        if (!open || !revisionId) return;
        let aborted = false;
        setLoading(true);
        setError("");
        setResult(null);
        (async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/scripts/${revisionId}/diff`,
                );
                if (!aborted) setDiff(data);
            } catch (err) {
                if (!aborted) setError(formatApiError(err));
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [open, projectId, revisionId]);

    // For revised-pages mode the omitted toggle is auto-enabled
    // based on omission flips in the `changed` bucket — see
    // omittedFlipCount below. Operator can still un-toggle it.
    // Iter-155A.1 — Previously this was hard-off + hidden, which
    // silently skipped real omissions delivered via yellow pages.

    const approve = async () => {
        // Iter-157P.8 Operation Pineapple closure (workflow trust).
        // Producer reported that pressing "Apply updated script"
        // marked the revision Done — but Scene 4's operational cast
        // remained stale until a second, hidden "Build Scenes & Cast"
        // press inside Scripts. Acceptance: "If the app says Done,
        // the project must actually be done." We now chain
        // approve → parse → confirm in one atomic-feeling flow.
        // Each step has its own try/catch so a downstream failure
        // surfaces honestly instead of pretending success.
        //
        // Iter-157P.18 — Apply step network resilience. Each call
        // now uses a 120s client-side timeout to wait past typical
        // Cloudflare ~100s edge windows, and on network failure we
        // run a single recovery probe before declaring the step
        // failed. This stops the "Apply showed an error but the
        // data actually applied correctly" mismatch the producer
        // hit during Meantime testing.
        const STEP_TIMEOUT = 120000;
        const probeRevisionStatus = async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/scripts/${revisionId}`,
                    { timeout: 15000 },
                );
                return (data?.status || "").toLowerCase();
            } catch {
                return null;
            }
        };
        const probeHasDrafts = async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/scenes`,
                    { params: { status: "draft" }, timeout: 15000 },
                );
                return Array.isArray(data) && data.length > 0;
            } catch {
                return false;
            }
        };
        const probeConfirmedSnapshot = async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/scenes/duplicates`,
                    { timeout: 15000 },
                );
                return data || null;
            } catch {
                return null;
            }
        };

        setSubmitting(true);
        setApplyPhase({ phase: "approving" });
        try {
            // Step 1 — approve the revision (becomes the new active).
            let approveData = null;
            try {
                const r = await api.post(
                    `${apiBase}/${projectId}/scripts/${revisionId}/approve`,
                    {
                        apply_added_scenes: applyAdded,
                        apply_omitted_scenes: applyOmitted,
                        apply_changed_headings: applyHeadings,
                    },
                    { timeout: STEP_TIMEOUT },
                );
                approveData = r.data;
            } catch (approveErr) {
                // Recovery probe: did approve already succeed
                // server-side? If the revision is now `active`,
                // the answer is yes and we can continue the chain.
                setApplyPhase({ phase: "recovering", message: "Checking whether the revision applied…" });
                const status = await probeRevisionStatus();
                if (status === "active" || status === "approved") {
                    approveData = { ok: true, recovered: true };
                } else {
                    setApplyPhase({
                        phase: "failed",
                        error:
                            "We couldn't apply this revision. Your project is safe — nothing has been changed.",
                    });
                    toast.error("We couldn't apply this revision. Your project is safe — nothing has been changed.", {
                        duration: Infinity,
                    });
                    return;
                }
            }

            // Iter-158.13.2.20c — revised_pages short-circuits the
            // parse+confirm chain.
            //
            // The mature approve → parse → confirm chain was designed
            // for full_script revisions where the revision doc IS the
            // whole episode and re-parsing + confirming produces the
            // correct final state.
            //
            // For revised_pages the revision doc contains ONLY the
            // changed pages. Re-parsing it would produce a partial
            // scene list, and the subsequent confirm-scenes step
            // treats that partial list as the whole episode and
            // deletes every non-matched confirmed scene — collapsing
            // e.g. 57 canonical scenes to 16 (Meantime Yellow Amends
            // UAT). Approve itself already performed the correct
            // incremental changes (marked scenes omitted, inserted
            // added scenes as confirmed via the .20c backend fix, and
            // ran the .20b `.17e` reconcile hook); nothing further is
            // needed.
            if (isPages) {
                setApplyPhase(null);
                const applied = approveData?.applied || {};
                const parts = ["Revised pages applied"];
                const added = (applied.added_scene_numbers || []).length;
                const omitted = (applied.marked_omitted_scene_numbers || []).length;
                const reinstated = (applied.reinstated_scene_numbers || []).length;
                const headings = (applied.updated_heading_scene_numbers || []).length;
                const detail = [];
                if (added) detail.push(`${added} added`);
                if (omitted) detail.push(`${omitted} marked omitted`);
                if (reinstated) detail.push(`${reinstated} reinstated`);
                if (headings) detail.push(`${headings} heading${headings === 1 ? "" : "s"} updated`);
                if (detail.length) parts.push(detail.join(" · "));
                toast.success(parts.join(" · "), {
                    description: "Existing scenes and continuity are untouched.",
                    duration: 10000,
                });
                setResult(approveData);
                if (onApproved) onApproved(approveData);
                return;
            }

            setApplyPhase({ phase: "parsing" });
            // Step 2 — re-parse the new active script so the latest
            // structural changes (additions, omissions, cast moves
            // via dialogue cues + action-line presence) become draft
            // scenes ready for confirm.
            //
            // Iter-157P.17 — call the SLOT-scoped parser
            // (/scripts/{revisionId}/parse) instead of the legacy
            // whole-project /script/parse. The slot parser reads
            // the script doc's `episode` field and stamps it on every
            // draft, which is essential for episodic projects:
            // without it, drafts went in with episode=null while the
            // existing confirmed scenes carried episode="2", causing
            // identity matching to silently null the episode label
            // and orphan unmatched confirmed scenes from the per-
            // episode delete query. That orphaning was the root
            // cause of the Meantime Pink Amends "98 scenes after
            // apply" duplication. After approve, the revision script
            // is now the active script, so /scripts/{revisionId}/parse
            // resolves to the right doc whether the project is
            // episodic or single-script.
            let parseData = null;
            try {
                const r = await api.post(
                    `${apiBase}/${projectId}/scripts/${revisionId}/parse`,
                    null,
                    { timeout: STEP_TIMEOUT },
                );
                parseData = r.data;
            } catch (parseErr) {
                // Recovery probe — drafts may already be in flight.
                setApplyPhase({ phase: "recovering", message: "Checking whether scenes were drafted…" });
                const hasDrafts = await probeHasDrafts();
                if (!hasDrafts) {
                    setApplyPhase({
                        phase: "failed",
                        error:
                            "Revision applied — but Build Scenes & Cast couldn't finish. Open Scripts and press Build Scenes & Cast manually. Your continuity is safe.",
                    });
                    setResult(approveData);
                    toast.warning("Revision applied — but Build Scenes & Cast failed", {
                        description:
                            "Open Scripts and press Build Scenes & Cast manually. Your continuity is safe.",
                        duration: 15000,
                    });
                    if (onApproved) onApproved(approveData);
                    return;
                }
                parseData = { recovered: true };
            }

            setApplyPhase({ phase: "confirming" });
            // Step 3 — promote drafts to confirmed so the operational
            // Scene / Cast views read the new state immediately.
            let confirmData = null;
            try {
                const r2 = await api.post(
                    `${apiBase}/${projectId}/scenes/confirm`,
                    null,
                    { timeout: STEP_TIMEOUT },
                );
                confirmData = r2.data;
            } catch (confirmErr) {
                setApplyPhase({ phase: "recovering", message: "Checking whether scenes were promoted…" });
                // We can't fully verify confirm succeeded just by
                // counting confirmed scenes (there are always some).
                // But we can surface the duplicates snapshot which is
                // a strong indicator that confirm at least ran.
                const snap = await probeConfirmedSnapshot();
                if (snap) {
                    confirmData = {
                        recovered: true,
                        duplicate_scene_groups: snap.duplicate_groups || [],
                    };
                } else {
                    setApplyPhase({
                        phase: "failed",
                        error:
                            "Revision applied — confirmation needs your attention. Open Scenes Review to finalize the new scenes list. Your continuity is safe.",
                    });
                    setResult(approveData);
                    toast.warning("Revision applied — confirmation needs your attention", {
                        description:
                            "Open Scenes Review to finalize the new scenes list. Your continuity is safe.",
                        duration: 15000,
                    });
                    if (onApproved) onApproved(approveData);
                    return;
                }
            }

            setApplyPhase(null);

            // Iter-157P.8 — done means done. Surface a single
            // confidence-restoring toast that summarises the whole
            // operation. Producer can navigate to Scene 4 and see
            // the new cast immediately.
            const sceneCount =
                parseData?.parsed_count ||
                parseData?.parsed_scenes_count ||
                0;
            const charsAdded = parseData?.new_characters_count || 0;
            const titleParts = [
                isPages ? "Revised pages applied" : "Revision applied",
                `${sceneCount} scenes built`,
            ];
            if (charsAdded) titleParts.push(`${charsAdded} new cast`);
            // Iter-157P.17 — critical apply-status toasts must remain
            // readable long enough for a producer to absorb them.
            // The default ~4s dismissal was the source of the
            // "toast disappeared too quickly to read" complaint
            // during Meantime testing.
            toast.success(titleParts.join(" · "), {
                description: "Scene cast is up to date.",
                duration: 10000,
            });

            // Iter-157P.17 — duplicate-scene safety net. The backend
            // surfaces `duplicate_scene_groups` whenever the confirmed
            // scenes table contains rows sharing (episode,
            // scene_number) after the apply chain completes. This is
            // the smoking-gun signal of the Meantime Pink Amends "98
            // scenes after apply" symptom: silent state corruption
            // from a partial apply chain. We surface it as a
            // persistent dismissable warning toast AND as inline copy
            // on the result block so the producer cannot miss it.
            const dupGroups = confirmData?.duplicate_scene_groups || [];
            if (dupGroups.length > 0) {
                const totalDup = dupGroups.reduce((acc, g) => acc + (g.count || 0), 0);
                toast.warning(
                    `Scene list shows ${totalDup} duplicate rows across ${dupGroups.length} scene number${dupGroups.length === 1 ? "" : "s"}`,
                    {
                        description:
                            "Your continuity is safe. Please contact the dev team with this project ID so the duplicates can be cleaned up — your scenes and notes are intact, the list is just doubled.",
                        duration: Infinity,
                    },
                );
            }

            // Iter-157P.8 — broadcast the same events the standalone
            // Build button would emit so any open Scene / Cast page
            // refetches without a manual reload.
            try {
                window.dispatchEvent(
                    new CustomEvent("osf:scenes-changed", {
                        detail: { projectId, source: "apply_updated_script" },
                    }),
                );
                window.dispatchEvent(
                    new CustomEvent("osf:scripts-changed", {
                        detail: { projectId, source: "apply_updated_script" },
                    }),
                );
            } catch (_) {
                /* no-op */
            }

            // Iter-150 — calm informational toast for any continuity
            // items the safety net could not confidently remap.
            const needsRe =
                (confirmData?.continuity_needs_reassignment || 0) +
                (parseData?.continuity_needs_reassignment || 0);
            if (needsRe > 0) {
                toast.message(
                    `${needsRe} continuity item${needsRe === 1 ? "" : "s"} need reassignment`,
                    {
                        description:
                            "Nothing was deleted — these items were detached during the rebuild and are kept safe.",
                    },
                );
            }

            setResult({ ...approveData, _build_complete: true });
            if (onApproved) onApproved({ ...approveData, _build_complete: true });
        } catch (err) {
            // Iter-157P.18 — terminal failure path. Make the inline
            // dialog banner show the same plain-English message we
            // emit via the persistent toast, never raw Cloudflare
            // / edge wording.
            setApplyPhase({
                phase: "failed",
                error:
                    "We couldn't finish applying this revision. Your project is safe — nothing has been changed.",
            });
            // Iter-157P.17 — critical error toasts must stay until
            // the producer dismisses them. Default sonner dismissal
            // (~4s) was too fast for upload/apply diagnostics.
            toast.error(formatApiError(err), {
                duration: Infinity,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const sceneDiff = diff?.diff?.scenes;
    const charSugg = diff?.diff?.characters;

    // Iter-155A.1 — Revised-pages mode delivers OMITTED via the
    // `changed` bucket (omitted: false→true on a common scene_number)
    // rather than the `removed` bucket. Count those flips so the
    // operator gets an explicit "Apply N omitted scenes" toggle even
    // in pages mode. Heading-only changes are NOT counted here.
    const omittedFlipChanges = (sceneDiff?.changed || []).filter(
        (c) =>
            (c.fields_changed || []).includes("omitted") &&
            (c.after?.omitted === true),
    );
    const reinstateFlipChanges = (sceneDiff?.changed || []).filter(
        (c) =>
            (c.fields_changed || []).includes("omitted") &&
            (c.after?.omitted === false),
    );
    const omittedTotal =
        (sceneDiff?.removed?.length || 0) + omittedFlipChanges.length;
    // Iter-157P.3 Operation Pineapple Finding #3 — Cast changes must
    // be surfaced explicitly per scene, not buried under "updated
    // headings". The diff puts every per-scene change (heading,
    // int_ext, time_of_day, omitted, characters) into the same
    // `changed` bucket with a generic `fields_changed` list; the
    // producer used to see "characters" as a label with no detail
    // on WHICH characters changed. Now we split: any scene whose
    // `fields_changed` includes "characters" is rendered in a
    // dedicated "Scenes with cast changes" section with explicit
    // `+ NAME` / `− NAME` deltas computed from before/after. A scene
    // that also has a heading change still appears in the heading
    // section — duplication is intentional so neither change hides.
    const nonOmittedChanges = (sceneDiff?.changed || []).filter(
        (c) => !(c.fields_changed || []).includes("omitted"),
    );
    const castChangeScenes = nonOmittedChanges
        .filter((c) => (c.fields_changed || []).includes("characters"))
        .map((c) => {
            const before = new Set(c.before?.characters || []);
            const after = new Set(c.after?.characters || []);
            const added = [...after].filter((n) => !before.has(n)).sort();
            const removed = [...before].filter((n) => !after.has(n)).sort();
            return { scene: c, added, removed };
        });
    const headingOnlyChanges = nonOmittedChanges.filter((c) => {
        const fields = c.fields_changed || [];
        return fields.some((f) =>
            f === "heading" || f === "int_ext" || f === "time_of_day"
        );
    });

    const headerTitle = isPages
        ? `Review changes${revisionLabel ? ` · ${revisionLabel}` : ""}`
        : `Review updated script${revisionLabel ? ` · ${revisionLabel}` : ""}`;

    const totalAffected = sceneDiff
        ? (sceneDiff.added?.length || 0) +
          (sceneDiff.removed?.length || 0) +
          (sceneDiff.changed?.length || 0)
        : 0;

    return (
        <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
            <DialogContent
                data-testid="script-revision-diff-dialog"
                className="max-w-2xl bg-card border-white/10 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        {isPages ? (
                            <Layers size={18} className="text-emerald-300" />
                        ) : (
                            <ShieldCheck size={18} className="text-amber-300" />
                        )}
                        {headerTitle}
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        {isPages
                            ? "Only the scenes referenced in these pages are being reviewed. Scenes outside these pages stay exactly as they are. Photos, notes and looks are always preserved — nothing falls into oblivion."
                            : "This is a full-script revision. Scenes missing from it will be flagged as removed. Nothing is deleted — removed scenes are marked omitted, and you can undo by editing the scene. Photos, notes and looks are always preserved."}
                    </DialogDescription>
                </DialogHeader>

                {applyPhase ? (() => {
                    // Iter-157P.18 — persistent in-dialog Apply
                    // banner. Stays visible across approve / parse /
                    // confirm step transitions and through recovery
                    // probes, so the producer always sees the
                    // current state. Never disappears mid-step.
                    const p = applyPhase;
                    if (p.phase === "failed") {
                        return (
                            <div
                                data-testid="script-revision-apply-failed-banner"
                                data-phase="failed"
                                className="rounded-lg border border-red-400/50 bg-red-500/10 p-3 flex items-start gap-3"
                            >
                                <AlertTriangle size={16} className="text-red-300 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] text-red-50 font-semibold leading-snug">
                                        We couldn&rsquo;t apply this revision
                                    </p>
                                    <p className="text-[11px] text-red-100/85 leading-relaxed mt-1">
                                        {p.error}
                                    </p>
                                </div>
                            </div>
                        );
                    }
                    const label =
                        p.phase === "approving"
                            ? "Applying revision…"
                            : p.phase === "parsing"
                                ? "Building scenes & cast…"
                                : p.phase === "confirming"
                                    ? "Finalising your scene list…"
                                    : p.phase === "recovering"
                                        ? (p.message || "Checking progress…")
                                        : "Working…";
                    const sub = p.phase === "recovering"
                        ? "We&rsquo;re checking whether the step already completed on the server."
                        : "This may take a moment for large revisions. Please keep this dialog open.";
                    return (
                        <div
                            data-testid="script-revision-apply-progress-banner"
                            data-phase={p.phase}
                            className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 flex items-start gap-3"
                        >
                            <Loader2 size={16} className="text-amber-200 animate-spin shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-amber-50 font-semibold leading-snug">
                                    {label}
                                </p>
                                <p
                                    className="text-[11px] text-amber-100/80 leading-relaxed mt-1"
                                    dangerouslySetInnerHTML={{ __html: sub }}
                                />
                            </div>
                        </div>
                    );
                })() : null}

                {loading ? (
                    <div className="py-10 flex items-center justify-center text-neutral-400 text-sm gap-2">
                        <Loader2 size={14} className="animate-spin" /> Comparing
                        scripts…
                    </div>
                ) : error ? (
                    <div
                        data-testid="script-revision-diff-error"
                        className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200"
                    >
                        {error}
                    </div>
                ) : result ? (
                    <ResultSummary
                        result={result}
                        isPages={isPages}
                        onClose={() => onOpenChange(false)}
                    />
                ) : sceneDiff ? (
                    <div className="space-y-4 mt-2">
                        {/* Iter-157P.11.5 — extraction-failure
                            warning. When the revision file
                            couldn't be parsed (zero scenes
                            extracted), surface a prominent
                            warning instead of silently flowing
                            existing scenes through "to be
                            omitted". Approve is blocked until the
                            producer fixes the upload (or
                            acknowledges the warning via the
                            developer payload, which the UI
                            doesn't expose). */}
                        {sceneDiff?.extraction_warning ? (() => {
                            const w = sceneDiff.extraction_warning;
                            const active = w.active_format;
                            const sceneCount = w.active_scene_count || 0;
                            const sceneWord = sceneCount === 1 ? "scene" : "scenes";
                            // Iter-157P.12 — producer-facing copy.
                            // Drops the parser-mechanics explanation
                            // entirely and answers the three questions
                            // the producer actually asks: what
                            // happened, is my project safe, what do I
                            // do now. Uses the active script's format
                            // to give a specific recovery action.
                            const recommendation = active
                                ? `Your original script was uploaded as a ${active} file. Please upload this revision as a ${active} file as well.`
                                : "Try re-exporting the revision (Final Draft → Print as PDF / Save as DOCX), or upload it in the same format as your original script.";
                            const fallback = active === "PDF"
                                ? "If the PDF still fails, export a fresh PDF from your screenwriting tool — the current one may have an unusual layout."
                                : active === "DOCX"
                                    ? null
                                    : "If you only have a PDF, try exporting a fresh PDF from your screenwriting tool and upload that.";
                            return (
                                <div
                                    data-testid="script-revision-extraction-warning"
                                    className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 space-y-2"
                                >
                                    <p className="text-[13px] text-red-100 font-semibold leading-snug">
                                        We couldn&rsquo;t read this revision
                                    </p>
                                    <p className="text-[12px] text-red-100/85 leading-relaxed">
                                        We found text in the uploaded file but couldn&rsquo;t reliably identify the scenes.
                                    </p>
                                    <p className="text-[12px] text-emerald-200 leading-relaxed">
                                        Your existing {sceneCount} {sceneWord}, cast and continuity are safe and cannot be changed by this revision.
                                    </p>
                                    <p className="text-[12px] text-red-100/85 leading-relaxed">
                                        <span className="font-medium">Recommended:</span>{" "}
                                        {recommendation}
                                    </p>
                                    {fallback ? (
                                        <p className="text-[11px] text-red-100/65 leading-relaxed">
                                            {fallback}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        })() : null}

                        {/* Iter-128 scope summary */}
                        <div
                            data-testid="script-revision-scope-summary"
                            className={`rounded-xl border p-3 ${
                                isPages
                                    ? "border-emerald-500/30 bg-emerald-500/5"
                                    : "border-amber-500/30 bg-amber-500/5"
                            }`}
                        >
                            <p className="text-[12px] text-neutral-200 leading-relaxed">
                                <span
                                    className={
                                        isPages
                                            ? "text-emerald-200 font-semibold"
                                            : "text-amber-200 font-semibold"
                                    }
                                >
                                    {totalAffected} scene
                                    {totalAffected === 1 ? "" : "s"} affected
                                </span>{" "}
                                · {sceneDiff.unchanged_count || 0} unchanged
                                {isPages && sceneDiff.out_of_scope_count
                                    ? ` · ${sceneDiff.out_of_scope_count} outside these pages (untouched)`
                                    : ""}
                            </p>
                        </div>

                        {/* Iter-155A.3 — Final Draft starred-pages
                            warning: the parser couldn't read a
                            screenplay scene number on N rows. Surface
                            this so the operator knows some rows were
                            dropped rather than misaligned. */}
                        {sceneDiff.dropped_positional_count > 0 ? (
                            <div
                                data-testid="script-revision-dropped-warning"
                                className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2"
                            >
                                <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
                                <p className="text-[12px] text-amber-100 leading-relaxed">
                                    <span className="font-semibold text-amber-200">
                                        {sceneDiff.dropped_positional_count} row
                                        {sceneDiff.dropped_positional_count === 1 ? "" : "s"} skipped
                                    </span>{" "}
                                    — these rows in the revised pages
                                    don&apos;t carry a recognisable scene
                                    number (Final Draft starred pages
                                    sometimes hide it). They were NOT
                                    matched against the active script to
                                    avoid mis-aligning changes onto the
                                    wrong scene. If you expected a scene
                                    here, check the page or re-export
                                    without revision asterisks.
                                </p>
                            </div>
                        ) : null}

                        {/* Apply toggles */}
                        <div
                            data-testid="script-revision-toggles"
                            className="rounded-xl border border-white/10 bg-secondary/30 p-3 space-y-3"
                        >
                            <p
                                data-testid="script-revision-what-happens-next-title"
                                className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold"
                            >
                                What happens next?
                            </p>
                            <ToggleRow
                                id="rev-apply-added"
                                checked={applyAdded}
                                onChange={setApplyAdded}
                                label={`Add ${sceneDiff.added.length} new scene${
                                    sceneDiff.added.length === 1 ? "" : "s"
                                }`}
                                hint="Inserted scenes (e.g. 52A) become first-class scenes in the project — searchable, openable, ready for continuity."
                                disabled={sceneDiff.added.length === 0}
                            />
                            <ToggleRow
                                id="rev-apply-omitted"
                                checked={applyOmitted}
                                onChange={setApplyOmitted}
                                label={`Mark ${omittedTotal} scene${
                                    omittedTotal === 1 ? "" : "s"
                                } as omitted`}
                                hint="Scenes stay visible everywhere with an 'Omitted by Revision' badge — photos, notes, looks and flags are never touched. Reinstate any time."
                                disabled={omittedTotal === 0}
                            />
                            <ToggleRow
                                id="rev-apply-headings"
                                checked={applyHeadings}
                                onChange={setApplyHeadings}
                                label={`Update scene headings for ${headingOnlyChanges.length} scene${
                                    headingOnlyChanges.length === 1 ? "" : "s"
                                }`}
                                hint="Manually edited scenes are SKIPPED automatically. Character cues are never auto-applied."
                                disabled={headingOnlyChanges.length === 0}
                            />
                        </div>

                        <RevisionReassurance
                            variant="compact"
                            testid="script-revision-reassurance"
                        />

                        <DiffSection
                            title="New scenes being added"
                            tone="emerald"
                            icon={<Plus size={12} />}
                            items={sceneDiff.added.map((s) => ({
                                key: s.scene_number,
                                primary: s.scene_number,
                                secondary: s.heading || "—",
                            }))}
                            testid="diff-added"
                        />
                        {!isPages ? (
                            <DiffSection
                                title="Scenes to be omitted (not deleted)"
                                tone="amber"
                                icon={<Minus size={12} />}
                                items={sceneDiff.removed.map((s) => ({
                                    key: s.scene_number,
                                    primary: s.scene_number,
                                    secondary: s.heading || "—",
                                }))}
                                testid="diff-removed"
                            />
                        ) : null}
                        {omittedFlipChanges.length > 0 ? (
                            <DiffSection
                                title="Scenes to be omitted (not deleted)"
                                tone="amber"
                                icon={<Minus size={12} />}
                                items={omittedFlipChanges.map((s) => ({
                                    key: `omit-${s.scene_number}`,
                                    primary: s.scene_number,
                                    secondary: s.before?.heading || "—",
                                }))}
                                testid="diff-omitted-flips"
                            />
                        ) : null}
                        {reinstateFlipChanges.length > 0 ? (
                            <DiffSection
                                title="Scenes to be reinstated"
                                tone="emerald"
                                icon={<Plus size={12} />}
                                items={reinstateFlipChanges.map((s) => ({
                                    key: `reinst-${s.scene_number}`,
                                    primary: s.scene_number,
                                    secondary: s.after?.heading || "—",
                                }))}
                                testid="diff-reinstate-flips"
                            />
                        ) : null}
                        {/* Iter-157P.3 Operation Pineapple Finding #3 —
                            Scenes with cast changes. Producer must
                            see explicit + / − deltas, not the generic
                            `fields_changed: ["characters"]` label.
                            Cast-only scenes were previously rendered
                            under "Scenes with updated headings" which
                            actively misled. */}
                        <DiffSection
                            title="Scenes with cast changes"
                            tone="violet"
                            icon={<Users size={12} />}
                            items={castChangeScenes.map(
                                ({ scene, added, removed }) => ({
                                    key: `cast-${scene.scene_number}`,
                                    primary: scene.scene_number,
                                    secondary: (
                                        <span
                                            className="text-[12px]"
                                            data-testid={`diff-cast-scene-${scene.scene_number}`}
                                        >
                                            <span className="text-neutral-300">
                                                Cast changed:
                                            </span>{" "}
                                            {added.map((n, i) => (
                                                <span
                                                    key={`a-${n}`}
                                                    className="text-emerald-300 font-semibold mr-2"
                                                    data-testid={`diff-cast-added-${scene.scene_number}-${n}`}
                                                >
                                                    + {n}
                                                    {i < added.length - 1 ||
                                                    removed.length > 0
                                                        ? " ·"
                                                        : ""}
                                                </span>
                                            ))}
                                            {removed.map((n, i) => (
                                                <span
                                                    key={`r-${n}`}
                                                    className="text-amber-300 font-semibold mr-2"
                                                    data-testid={`diff-cast-removed-${scene.scene_number}-${n}`}
                                                >
                                                    − {n}
                                                    {i < removed.length - 1
                                                        ? " ·"
                                                        : ""}
                                                </span>
                                            ))}
                                            <span className="text-neutral-500">
                                                · {scene.after?.heading || "—"}
                                            </span>
                                        </span>
                                    ),
                                }),
                            )}
                            testid="diff-cast-changed"
                            footer="Cast attaches when you press Build Scenes & Cast — review the deltas above first."
                        />
                        <DiffSection
                            title="Scenes with updated headings"
                            tone="sky"
                            icon={<Pencil size={12} />}
                            items={headingOnlyChanges.map((s) => ({
                                key: s.scene_number,
                                primary: s.scene_number,
                                secondary: (
                                    <span className="text-[11px] text-neutral-400">
                                        {(s.fields_changed || [])
                                            .filter(
                                                (f) => f !== "characters",
                                            )
                                            .join(" · ")}{" "}
                                        ·{" "}
                                        <span className="text-neutral-300">
                                            {s.after?.heading || "—"}
                                        </span>
                                    </span>
                                ),
                            }))}
                            testid="diff-changed"
                        />
                        <DiffSection
                            title="Character suggestions (not auto-applied)"
                            tone="violet"
                            icon={<Users size={12} />}
                            items={[
                                ...(charSugg?.added || []).map((n) => ({
                                    key: `add-${n}`,
                                    primary: "+",
                                    secondary: n,
                                })),
                                ...(charSugg?.removed || []).map((n) => ({
                                    key: `rem-${n}`,
                                    primary: "−",
                                    secondary: n,
                                })),
                            ]}
                            testid="diff-characters"
                            footer="Add or remove characters yourself from Cast Hub when ready."
                        />

                        <div
                            data-testid="script-revision-safety-banner"
                            className="rounded-md border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-3 text-[13px] text-emerald-900 dark:text-emerald-50 flex items-start gap-2"
                        >
                            <ShieldCheck
                                size={13}
                                className="text-emerald-700 dark:text-emerald-200 mt-0.5 shrink-0"
                            />
                            <span>
                                Photos, per-pair notes, looks and manual scene
                                edits are never overwritten. Nothing is
                                deleted — removed scenes are marked omitted and
                                can be restored at any time.
                            </span>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                data-testid="script-revision-cancel"
                                variant="secondary"
                                onClick={() => onOpenChange(false)}
                                disabled={submitting}
                                className="flex-1 min-tap"
                            >
                                Cancel review
                            </Button>
                            <Button
                                type="button"
                                data-testid="script-revision-approve"
                                onClick={approve}
                                disabled={submitting}
                                className="flex-1 min-tap font-semibold"
                            >
                                {submitting ? (
                                    <span className="inline-flex items-center gap-2">
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                        Applying changes…
                                    </span>
                                ) : isPages ? (
                                    "Apply changes"
                                ) : (
                                    "Apply updated script"
                                )}
                            </Button>
                        </div>
                        {sceneDiff?._debug && process.env.NODE_ENV !== "production" ? (
                            <div className="pt-2 border-t border-white/5">
                                <p className="text-[11px] text-neutral-500 leading-snug mb-2">
                                    Diagnostics — tap to copy the resolver&rsquo;s
                                    decision payload (used to tune scene
                                    identity matching for your scripts).
                                </p>
                                <Button
                                    type="button"
                                    data-testid="script-revision-copy-debug"
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                        const payload = {
                                            iteration: "iter-157p.11.4",
                                            captured_at: new Date().toISOString(),
                                            revision_kind: diff?.diff?.revision_kind,
                                            scenes_summary: {
                                                added_count: sceneDiff.added?.length || 0,
                                                removed_count: sceneDiff.removed?.length || 0,
                                                changed_count: sceneDiff.changed?.length || 0,
                                                unchanged_count: sceneDiff.unchanged_count || 0,
                                                auto_matched_count: sceneDiff.auto_matched?.length || 0,
                                                pending_identity_count: sceneDiff.pending_identity_matches?.length || 0,
                                            },
                                            added: sceneDiff.added || [],
                                            removed: sceneDiff.removed || [],
                                            changed: sceneDiff.changed || [],
                                            auto_matched: sceneDiff.auto_matched || [],
                                            pending_identity_matches: sceneDiff.pending_identity_matches || [],
                                            characters: diff?.diff?.characters || {},
                                            _debug: sceneDiff._debug,
                                        };
                                        const text = JSON.stringify(payload, null, 2);
                                        try {
                                            if (navigator.clipboard?.writeText) {
                                                await navigator.clipboard.writeText(text);
                                            } else {
                                                // Fallback for non-secure contexts.
                                                const ta = document.createElement("textarea");
                                                ta.value = text;
                                                document.body.appendChild(ta);
                                                ta.select();
                                                document.execCommand("copy");
                                                document.body.removeChild(ta);
                                            }
                                            setDebugCopied(true);
                                            setTimeout(() => setDebugCopied(false), 2500);
                                        } catch {
                                            // Last-ditch fallback: open a tab
                                            // with the JSON so the producer
                                            // can copy it manually.
                                            const blob = new Blob([text], { type: "application/json" });
                                            const url = URL.createObjectURL(blob);
                                            window.open(url, "_blank");
                                        }
                                    }}
                                    className="w-full text-xs justify-center gap-2"
                                >
                                    {debugCopied
                                        ? "Copied — paste it back to the dev team"
                                        : "Copy debug payload"}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function ToggleRow({ id, checked, onChange, label, hint, disabled }) {
    return (
        <div className="flex items-start gap-3">
            <Switch
                id={id}
                data-testid={id}
                checked={checked}
                onCheckedChange={onChange}
                disabled={disabled}
                className="mt-0.5"
            />
            <div className="min-w-0">
                <Label
                    htmlFor={id}
                    className={`text-sm ${
                        disabled ? "text-neutral-500" : "text-white"
                    } font-medium`}
                >
                    {label}
                </Label>
                <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                    {hint}
                </p>
            </div>
        </div>
    );
}

const TONE_CLASS = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-200",
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-200",
    violet: "border-violet-500/30 bg-violet-500/5 text-violet-200",
};

function DiffSection({ title, tone, icon, items, testid, footer }) {
    if (!items || items.length === 0) return null;
    return (
        <div
            data-testid={testid}
            className={`rounded-xl border ${TONE_CLASS[tone]} p-3`}
        >
            <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold">
                {icon}
                {title}
                <span className="ml-auto text-[10px] opacity-70">
                    {items.length}
                </span>
            </h3>
            <ul className="mt-2 space-y-1.5">
                {items.map((it) => (
                    <li
                        key={it.key}
                        className="flex items-start gap-2 text-sm text-neutral-100"
                    >
                        <span className="mono shrink-0 w-8 text-neutral-300">
                            {it.primary}
                        </span>
                        <span className="min-w-0 break-words">
                            {it.secondary}
                        </span>
                    </li>
                ))}
            </ul>
            {footer ? (
                <p className="mt-2 text-[11px] text-neutral-400 italic">
                    {footer}
                </p>
            ) : null}
        </div>
    );
}

function ResultSummary({ result, isPages, onClose }) {
    const a = result.applied || {};
    const s = result.summary || {};
    const insertedCount = s.inserted ?? (a.added_scene_ids?.length || 0);
    const omittedCount = s.omitted ?? (a.marked_omitted_scene_ids?.length || 0);
    const reinstatedCount = s.reinstated ?? (a.reinstated_scene_ids?.length || 0);
    const updatedCount = s.updated ?? (a.updated_heading_scene_ids?.length || 0);
    const skippedManualCount = s.skipped_manually_edited ?? (a.skipped_manually_edited?.length || 0);
    const skippedExistingCount = s.skipped_added_existing ?? (a.skipped_added_existing?.length || 0);
    // Iter-155B.0 — plain-English result. Backend now returns
    // `*_scene_numbers` arrays alongside `*_scene_ids` so we can
    // print "Scene 52A" instead of opaque UUIDs.
    const addedNums = a.added_scene_numbers || [];
    const omittedNums = a.marked_omitted_scene_numbers || [];
    const reinstatedNums = a.reinstated_scene_numbers || [];
    const updatedNums = a.updated_heading_scene_numbers || [];
    return (
        <div
            data-testid="script-revision-result"
            className="space-y-3 mt-2"
        >
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-4 flex items-start gap-3">
                <CheckCircle2
                    size={18}
                    className="text-emerald-700 dark:text-emerald-200 mt-0.5 shrink-0"
                />
                <div>
                    <p
                        className="text-emerald-900 dark:text-emerald-50 font-semibold"
                        data-testid="script-revision-result-title"
                    >
                        {result?._build_complete
                            ? "Revision applied · scenes & cast updated"
                            : "Revision applied"}
                    </p>
                    <p
                        className="text-[13px] text-emerald-900 dark:text-emerald-50 mt-1"
                        data-testid="script-revision-result-headline"
                    >
                        {/* Iter-155A.1 — keep the explicit audit
                            headline for tests + at-a-glance reading. */}
                        {`${insertedCount} scene${insertedCount === 1 ? "" : "s"} inserted · ${omittedCount} scene${omittedCount === 1 ? "" : "s"} omitted · ${updatedCount} updated`}
                        {reinstatedCount > 0
                            ? ` · ${reinstatedCount} reinstated`
                            : ""}
                    </p>
                </div>
            </div>

            {/* Iter-155B.0 — named lists. Show even when empty so the
                operator always sees the same trust-layer structure. */}
            <NamedSceneList
                testid="revision-result-added-list"
                label="Added"
                tone="emerald"
                nums={addedNums}
                emptyText={insertedCount === 0 ? "No new scenes." : null}
                fallbackCount={insertedCount}
            />
            <NamedSceneList
                testid="revision-result-omitted-list"
                label="Marked Omitted"
                tone="amber"
                nums={omittedNums}
                emptyText={omittedCount === 0 ? "No scenes omitted." : null}
                fallbackCount={omittedCount}
            />
            {reinstatedCount > 0 ? (
                <NamedSceneList
                    testid="revision-result-reinstated-list"
                    label="Reinstated"
                    tone="emerald"
                    nums={reinstatedNums}
                    fallbackCount={reinstatedCount}
                />
            ) : null}
            <NamedSceneList
                testid="revision-result-updated-list"
                label="Headings updated"
                tone="sky"
                nums={updatedNums}
                emptyText={updatedCount === 0 ? "No scene headings updated." : null}
                fallbackCount={updatedCount}
            />

            {/* Things that need the operator's attention. Only shown
                when non-zero so we don't add noise. */}
            {skippedManualCount > 0 || skippedExistingCount > 0 ? (
                <div
                    data-testid="revision-result-needs-attention"
                    className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2"
                >
                    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-200">
                        Needs your attention
                    </p>
                    {skippedManualCount > 0 ? (
                        <p
                            data-testid="script-revision-result-skipped-manual"
                            className="text-[12px] text-amber-50"
                        >
                            {skippedManualCount} scene
                            {skippedManualCount === 1 ? "" : "s"} skipped
                            because you've manually edited them. Open
                            them to apply the revision yourself.
                        </p>
                    ) : null}
                    {skippedExistingCount > 0 ? (
                        <p
                            data-testid="script-revision-result-skipped-existing"
                            className="text-[12px] text-amber-50/90"
                        >
                            {skippedExistingCount} new scene
                            {skippedExistingCount === 1 ? "" : "s"} already
                            existed and were left as they are.
                        </p>
                    ) : null}
                </div>
            ) : null}

            <RevisionReassurance
                variant="compact"
                testid="script-revision-result-reassurance"
            />
            <p className="text-[11px] text-neutral-500 italic px-1">
                {isPages
                    ? "The current script remains active. Scenes outside these pages are untouched."
                    : "Active script switched to this revision. The previous version is archived (kept for audit)."}
            </p>

            <Button
                type="button"
                data-testid="script-revision-result-close"
                onClick={onClose}
                className="w-full min-tap"
            >
                Done
            </Button>
        </div>
    );
}

function NamedSceneList({
    testid,
    label,
    tone,
    nums,
    emptyText,
    fallbackCount,
}) {
    // tone: emerald | amber | sky
    const toneClass =
        tone === "amber"
            ? "border-amber-500/30 bg-amber-500/5"
            : tone === "sky"
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-emerald-500/30 bg-emerald-500/5";
    const dotClass =
        tone === "amber"
            ? "text-amber-300"
            : tone === "sky"
                ? "text-sky-300"
                : "text-emerald-300";
    const total = (nums && nums.length) || fallbackCount || 0;
    return (
        <div
            data-testid={testid}
            className={`rounded-xl border ${toneClass} p-3`}
        >
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-neutral-200 flex items-center gap-2">
                <span>{label}</span>
                <span className="text-[10px] text-neutral-400">({total})</span>
            </p>
            {nums && nums.length > 0 ? (
                <ul
                    data-testid={`${testid}-items`}
                    className="mt-2 space-y-0.5 text-sm text-white"
                >
                    {nums.map((n) => (
                        <li
                            key={`${testid}-${n}`}
                            data-testid={`${testid}-item-${n}`}
                            className="flex items-center gap-2"
                        >
                            <span className={dotClass}>•</span>
                            <span>Scene {n}</span>
                        </li>
                    ))}
                </ul>
            ) : emptyText ? (
                <p className="mt-1 text-[12px] text-neutral-500 italic">
                    {emptyText}
                </p>
            ) : null}
        </div>
    );
}
